import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_COPY_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

import { DEFAULT_SHARE_CONTEXT_LEVEL } from './shareContextPrompt.ts';

const i18n = defineMessages({
	copyPrompt: {
		id: 'byline-connections.share-context-menu.copy-prompt',
		defaultMessage: 'Copy as prompt',
		description:
			'Item in the "Share with agent" menu that copies a prompt carrying this page’s context, for pasting into an AI tool the menu does not link to.',
	},
});

export type ShareContextCopyPromptItemProps = {
	prompt: string;
};

/** Copies the same prompt the deep links carry, for surfaces the menu does not link to. */
export function ShareContextCopyPromptItem({
	prompt,
}: ShareContextCopyPromptItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	// The event waits for the write to resolve so it never claims a copy that did not happen —
	// an unsupported or blocked clipboard rejects here.
	const handleClick = useCallback(() => {
		const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
		void clipboard
			?.writeText(prompt)
			.then(() =>
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: SHARE_CONTEXT_COPY_ACTION_SUBJECT_ID,
						source: ANALYTICS_SOURCE,
						attributes: { contextLevel: DEFAULT_SHARE_CONTEXT_LEVEL },
					},
				}).fire(),
			)
			.catch(() => {
				// Nothing was copied, so there is nothing to report.
			});
	}, [createAnalyticsEvent, prompt]);

	return (
		<DropdownItem
			elemBefore={<ClipboardIcon label="" />}
			onClick={handleClick}
			testId="share-context-menu-copy-prompt"
		>
			{formatMessage(i18n.copyPrompt)}
		</DropdownItem>
	);
}
